import { supabase } from '../lib/supabase';
import { UserRole, UserProfile } from '../models/User';
export class AuthService {
    static async getAuthenticatedUser() {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error)
            throw error;
        if (!user)
            throw new Error('User not authenticated');
        return user;
    }
    static async signUp(data, platform = 'web') {
        const { data: authData, error } = await supabase.auth.signUp({
            email: data.email,
            password: data.password,
            options: {
                data: {
                    first_name: data.first_name,
                    last_name: data.last_name,
                    platform: platform
                }
            }
        });
        if (error) {
            console.error('Sign up error:', error);
            throw new Error(error.message || 'Unable to create account');
        }
        if (authData.user) {
            try {
                const { error: profileError } = await supabase.rpc('create_user_profile', {
                    user_id: authData.user.id,
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    platform: platform
                });
                if (profileError) {
                    console.error('Profile creation failed:', profileError);
                }
            }
            catch (profileErr) {
                console.error('Profile creation exception:', profileErr);
            }
        }
        return authData;
    }
    static async signIn(email, password) {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        if (error) {
            throw new Error(error.message || 'Unable to sign in');
        }
        return data;
    }
    static async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error)
            throw error;
    }
    static async resendConfirmation(email, platform = 'web') {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email: email
        });
        if (error) {
            console.error('Resend confirmation error:', error);
            throw new Error(error.message || 'Unable to send confirmation email');
        }
    }
    static async getCurrentUser() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !user.email)
            return null;
        const { data: profileData } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();
        return {
            id: user.id,
            email: user.email,
            created_at: user.created_at,
            profile: profileData ? new UserProfile(profileData) : undefined
        };
    }
    static async getUserProfile(userId) {
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .single();
        if (error)
            return null;
        return data;
    }
    static async updateUserProfile(userId, updates) {
        const { data, error } = await supabase
            .from('user_profiles')
            .update(updates)
            .eq('id', userId)
            .select()
            .single();
        if (error)
            throw error;
        return data;
    }
    static async changeUserRoles(targetUserId, newRoles, adminUserId) {
        const { error } = await supabase
            .from('user_profiles')
            .update({ roles: newRoles })
            .eq('id', targetUserId);
        if (error)
            throw error;
    }
    static async getCurrentSession() {
        const { data: { session } } = await supabase.auth.getSession();
        return session;
    }
    // Access control methods using UserProfile helper patterns
    static canAccessWebApp(profile) {
        return (profile.roles.includes(UserRole.Clinician) ||
            profile.roles.includes(UserRole.StudyAdmin) ||
            profile.roles.includes(UserRole.WebAdmin));
    }
    static canAccessMobileApp(profile) {
        return (profile.roles.includes(UserRole.Patient) ||
            profile.roles.includes(UserRole.Clinician));
    }
    static canAccessStudyAdminPanel(profile) {
        return profile.roles.includes(UserRole.StudyAdmin);
    }
    static canAccessWebAdminPanel(profile) {
        return profile.roles.includes(UserRole.WebAdmin);
    }
    // Additional utility methods
    static async resetPassword(email, platform = 'web', redirectTo) {
        const defaultRedirectTo = platform === 'mobile' ? 'luraapp://reset-password/' : redirectTo;
        const { error } = await supabase.auth.resetPasswordForEmail(email, defaultRedirectTo ? { redirectTo: defaultRedirectTo } : {});
        if (error) {
            throw new Error(error.message || 'Unable to send reset password email');
        }
    }
    static async updatePassword(password) {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) {
            throw new Error(error.message || 'Unable to update password');
        }
    }
    static async verifyOtp(email, token, type = 'email') {
        const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type
        });
        if (error) {
            throw new Error(error.message || 'Unable to verify OTP');
        }
        return data;
    }
}
